import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Box, ButtonBase, IconButton } from "@mui/material";
import { ReactNode, useRef, useState } from "react";
import Flex from "../Flex";
import Text from "../Text";
import { VisuallyHiddenInput } from "../VisuallyHiddenInput";

/** `accept` sentinel meaning "any file type"; such consumers skip previews. */
const ALL_FILE_TYPES = "all";
const IMAGE_MIME_PREFIX = "image/";

type DropZoneProps<T extends boolean> = {
  supportedFile?: string;
  backgroundColor?: string;
  label?: string;
  content: string;
  height?: string | number;
  width?: string | number;
  uploadIcon?: ReactNode;
  solidBorder?: boolean;
  value?: string;
  fileName?: string;
  onClick?: (file: FileList | null) => void;
  multiple?: T;
  labelComponent?: ReactNode;
  error?: string | boolean;
  accept?: string;
  required?: boolean;
  titleComponent?: ReactNode;
};

const DropZone = <T extends boolean>({
  value,
  fileName,
  onClick,
  label,
  content,
  height = "100%",
  width = "100%",
  labelComponent,
  required,
  uploadIcon,
  backgroundColor,
  solidBorder,
  multiple,
  error,
  supportedFile,
  accept = ".jpg,.jpeg,.png,.svg",
  titleComponent,
}: DropZoneProps<T>) => {
  const [previewFiles, setPreviewFiles] =
    useState<{ name: string; avatar: string; isImage: boolean }[]>();
  const handleFileChange = (file: File) => {
    return new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFiles = async (file: File | null) => {
    if (!file) return;

    const previews = await handleFileChange(file);

    setPreviewFiles([
      {
        name: file.name,
        avatar: previews,
        isImage: file.type.startsWith(IMAGE_MIME_PREFIX),
      },
    ]);
  };

  const isPreviewVisible = previewFiles && previewFiles?.length > 0;
  const inputRef = useRef<HTMLInputElement | null>(null);

  const getUrlPreviewKind = (url: string): "image" | "pdf" | "unknown" => {
    let pathname = url;
    try {
      pathname = new URL(url).pathname;
    } catch {
      pathname = url.split("?")[0];
    }
    const ext = pathname.toLowerCase().split(".").pop() ?? "";
    if (["jpg", "jpeg", "png", "gif", "svg", "webp", "bmp"].includes(ext))
      return "image";
    if (ext === "pdf") return "pdf";
    if (url.startsWith("http")) return "image";
    return "unknown";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();

    const file = e.dataTransfer?.files?.item?.(0);
    if (file) handleFiles(file);

    onClick?.(e?.dataTransfer?.files);
  };

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      sx={{
        width: "100%",
      }}
    >
      {labelComponent && (
        <Box
          sx={{
            mb: "10px",
          }}
        >
          {labelComponent}
        </Box>
      )}

      {label && !labelComponent && (
        <Text mb={"4px"} color={"info.main"}>
          {label}
          {required && (
            <Text fontSize={"13px"} component={"span"} color={"error"}>
              {` *`}
            </Text>
          )}
        </Text>
      )}

      <Box
        sx={{
          position: "relative",
        }}
      >
        <ButtonBase
          sx={{
            bgcolor: backgroundColor,
            height: height,
            width: width,
            border: `1px ${solidBorder ? "solid" : "dashed"} ${
              error ? "red" : "#1A1A1A66"
            }`,
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            borderRadius: 2.5,
          }}
          component="label"
        >
          {!isPreviewVisible &&
            !value &&
            (uploadIcon ?? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "grey.200",
                  borderRadius: "50%",
                  p: 1.5,
                }}
              >
                <FileUploadOutlinedIcon
                  sx={{ fontSize: "2em", color: "black" }}
                />
              </Box>
            ))}

          <VisuallyHiddenInput
            ref={inputRef}
            key={value}
            multiple={multiple}
            onChange={(e) => {
              const file = e.target.files?.item?.(0);
              if (file) handleFiles(file);

              onClick?.(e?.target?.files);
            }}
            type="file"
            accept={accept !== "all" ? accept : undefined}
          />
          {/* <Text color={"primary.main"} px={4} textAlign={"center"}> */}
          {/* show preview file names after upload file */}
          {previewFiles?.map((it) => (
            <Flex
              key={it?.name}
              justifyContent={"center"}
              alignItems={"center"}
              width={width}
              height={height}
            >
              {/* Only an actual image can be rendered inline. A CSV, XLSX, PDF
                  or EDI file would otherwise fall back to a broken <img> that
                  shows its alt text, so name those files instead.
                  `accept="all"` consumers opt out of previews entirely. */}
              {it?.isImage && accept !== ALL_FILE_TYPES ? (
                <img
                  style={{ objectFit: "contain", borderRadius: 10 }}
                  src={it?.avatar}
                  height={"100%"}
                  width={"100%"}
                  alt={`${it?.name ?? ""} - image`}
                />
              ) : (
                <Text sx={{ wordBreak: "break-all", px: 2 }}>{it?.name}</Text>
              )}
            </Flex>
          ))}

          {!isPreviewVisible &&
            value &&
            accept !== ".csv" &&
            accept !== "all" &&
            (() => {
              const kind = getUrlPreviewKind(value);
              if (kind === "pdf") {
                return (
                  <Box
                    sx={{
                      width: width,
                      height: height,
                    }}
                  >
                    <iframe
                      src={value}
                      title="attachment-preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                        borderRadius: 10,
                      }}
                    />
                  </Box>
                );
              }
              if (kind === "image") {
                return (
                  <Box
                    sx={{
                      width: width,
                      height: height,
                    }}
                  >
                    <img
                      style={{ objectFit: "contain", borderRadius: 10 }}
                      src={value}
                      height={"100%"}
                      width={"100%"}
                    />
                  </Box>
                );
              }
              return (
                <Flex
                  justifyContent={"center"}
                  alignItems={"center"}
                  width={width}
                  height={height}
                  px={2}
                >
                  <Text sx={{ textAlign: "center", wordBreak: "break-all" }}>
                    {fileName || value}
                  </Text>
                </Flex>
              );
            })()}

          {value &&
            !previewFiles?.length &&
            (accept === ".csv" || accept === "all") &&
            value}

          {!isPreviewVisible &&
            !value &&
            (titleComponent ?? (
              <Text sx={{ textAlign: "center" }}>{content}</Text>
            ))}
        </ButtonBase>

        {value && (
          <IconButton
            onClick={() => {
              setPreviewFiles(undefined);
              onClick?.(null);
            }}
            sx={{
              position: "absolute",
              top: -4,
              right: -18,
              height: "10px",
            }}
          >
            <HighlightOffIcon color="error" fontSize="medium" />
          </IconButton>
        )}
      </Box>

      {!!error && (
        <Text variant="caption" color={"error"}>
          {error}
        </Text>
      )}
      {!error && !!supportedFile && (
        <Text variant="subtitle2">{`Supported formats ${supportedFile}`}</Text>
      )}
    </Box>
  );
};

export default DropZone;
